import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { MENU_ITEMS_BY_ID } from "@/data/menu";

const CartContext = createContext(null);
const STORAGE_KEY = "drago_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [open, setOpen] = useState(false);
  // Table number from QR (?table=X) - persisted for the session
  const [tableFromQR, setTableFromQR] = useState(() => {
    try {
      const url = new URL(window.location.href);
      const t = url.searchParams.get("table");
      if (t) {
        sessionStorage.setItem("drago_table", t);
        return t;
      }
      return sessionStorage.getItem("drago_table") || "";
    } catch { return ""; }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const cartKey = (id, option) => `${id}::${option || ""}`;

  const add = useCallback((id, option) => {
    setItems((prev) => {
      const key = cartKey(id, option);
      const idx = prev.findIndex((it) => cartKey(it.id, it.option) === key);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: next[idx].qty + 1 };
        return next;
      }
      return [...prev, { id, option: option || null, qty: 1 }];
    });
  }, []);

  const dec = useCallback((id, option) => {
    setItems((prev) => {
      const key = cartKey(id, option);
      const idx = prev.findIndex((it) => cartKey(it.id, it.option) === key);
      if (idx < 0) return prev;
      const next = [...prev];
      if (next[idx].qty <= 1) {
        next.splice(idx, 1);
      } else {
        next[idx] = { ...next[idx], qty: next[idx].qty - 1 };
      }
      return next;
    });
  }, []);

  const remove = useCallback((id, option) => {
    setItems((prev) => prev.filter((it) => cartKey(it.id, it.option) !== cartKey(id, option)));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const enriched = useMemo(() => {
    return items
      .map((it) => {
        const meta = MENU_ITEMS_BY_ID[it.id];
        if (!meta) return null;
        return { ...it, meta, subtotal: meta.price * it.qty };
      })
      .filter(Boolean);
  }, [items]);

  const totals = useMemo(() => {
    const count = enriched.reduce((s, it) => s + it.qty, 0);
    const price = enriched.reduce((s, it) => s + it.subtotal, 0);
    return { count, price };
  }, [enriched]);

  const value = { items: enriched, add, dec, remove, clear, totals, open, setOpen, tableFromQR };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
